/**
 * English locale — default language
 */
export default {
  // Common / shared
  common: {
    save: 'Save',
    saving: 'Saving...',
    saved: '✓ Saved',
    cancel: 'Cancel',
    confirm: 'Confirm',
    delete: 'Delete',
    deleting: 'Deleting...',
    edit: 'Edit',
    add: 'Add',
    remove: 'Remove',
    close: 'Close',
    copy: 'Copy',
    search: 'Search...',
    loading: 'Loading...',
    noData: 'No data',
    send: 'Send',
    stop: '⏹ Stop',
    restore: 'Restore',
    archive: 'Archive',
    new: 'New',
    all: 'All',
    back: 'Back',
    update: 'Update',
    create: 'Create',
    done: 'Done',
    failed: 'Failed',
    active: 'Active',
    inactive: 'Inactive',
    online: 'Online',
    offline: 'Offline',
    pending: 'Pending',
    running: 'Running...',
    complete: 'Complete',
    completed: 'Completed',
    cancelled: 'Cancelled',
    accepted: 'Accepted',
    rejected: 'Rejected',
    published: 'Published',
    draft: 'Draft',
    archived: 'Archived',
    deleted: 'Deleted',
    updated: 'Updated',
    important: 'Important',
    urgent: 'Urgent',
    overview: 'Overview',
    noMatchingRows: 'No matching rows',
    noRecordsFound: 'No records found',
    saveFailed: 'Save failed',
    deleteFailed: 'Delete failed',
    loadFailed: 'Failed to load',
    openInNewTab: 'Open in new tab',
    tryDifferentSearch: 'Try a different search or category',
    tryAdjustFilters: 'Try adjusting your filters',
    star: 'Star',
    unstar: 'Unstar',
    untitled: 'Untitled',
    collapse: 'Collapse',
    expand: 'Expand',
    empty: '(empty)',
    notAnswered: '(not answered)',
    applyingChanges: 'Applying...',
    comment: 'Comment',
    networkError: 'Network error',
    createFailed: 'Create failed',
    operationFailed: 'Operation failed',
    neverLoggedIn: 'Never logged in',
    status: 'Status',
    priority: 'Priority',
    assignee: 'Assignee',
    unassigned: 'Unassigned',
    self: 'Self',
    startDate: 'Start Date',
    endDate: 'End Date',
    dueDate: 'Due Date',
    description: 'Description',
    name: 'Name',
    nickname: 'Nickname',
    email: 'Email',
    role: 'Role',
    color: 'Color',
    tags: 'Tags',
    refresh: 'Refresh',
    moreActions: 'More Actions',
    viewDetails: 'View Details',
    noRecords: 'No records yet',
    you: 'You',
    select: 'Select',
    selectProject: 'Select project',
    selected: '{count} selected',
    batchUpdateStatus: 'Batch update status:',
    assign: 'Assign:',
    moveTo: 'Move to:',
    cancelSelection: '✕ Cancel',
    searchTasks: 'Search tasks...',
    total: 'Total {count}',
  },

  // App shell / navigation
  app: {
    chat: 'Chat',
    projects: 'Projects',
    settings: 'Settings',
    noPermission: "You don't have permission to access this page.",
    myProfile: 'My Profile',
    preferences: 'Preferences',
    prompts: 'Prompts',
    knowledge: 'Knowledge',
    logout: 'Logout',
    logoutTitle: 'Log out?',
    logoutDesc: 'You will be signed out of your account and need to log in again to continue.',
    agentPanel: 'Global Agent (⌘K)',
  },

  // Login (product name comes from the branding seam, not i18n)
  login: {
    subtitle: 'Sign in to continue',
    teamLogin: 'Team Login',
    guestAccess: 'Guest Access',
    emailPlaceholder: 'Email',
    password: 'Password',
    accessCode: 'Access Code',
    signIn: 'Sign In',
    enter: 'Enter',
    signingIn: 'Signing in...',
    emailRequired: 'Email is required',
    loginFailed: 'Login failed',
    invalidPassword: 'Invalid password',
  },

  // Preferences
  preferences: {
    title: 'Preferences',
    theme: 'Theme',
    themeLight: 'Light',
    themeDark: 'Dark',
    themeSystem: 'System',
    language: 'Language',
    personalNotes: 'Personal Notes',
    notesHint:
      "Your notes will be injected into the AI Agent's context so it can personalize responses for you. For example: your role, preferred language, communication style, or areas of focus.",
    notesPlaceholder: 'e.g.\nI am a product manager.\nPlease respond in Chinese.\nKeep answers concise and technical.',
    charactersRemaining: '{count} characters remaining',
  },

  // Branding Studio (super only)
  brandingStudio: {
    intro:
      'Shape how this workspace looks — pick a starting style, then fine-tune colour, type and shape with everything previewing live on the right. When it feels right, save it to the workspace (name, logo and team Sprouty included) so everyone gets it from the login screen on — or export the CSS for a fork.',

    // Presets
    presets: 'Starting styles',
    preset_greenhouse: 'Greenhouse',
    preset_slate: 'Slate',
    preset_sunset: 'Sunset',
    preset_editorial: 'Editorial',
    preset_graphite: 'Graphite',

    // Sections
    brandColor: 'Brand colour',
    semantic: 'Status colours',
    tokens: 'Surfaces, text & borders',
    typography: 'Typography',
    shape: 'Corners',
    groupSurface: 'Surfaces',
    groupText: 'Text',
    groupBorder: 'Borders',

    // Controls
    fontDefault: 'System default',
    fontCustomPlaceholder: "Or type a font stack — 'Brand Sans', system-ui, sans-serif",
    fontMonoPlaceholder: "Monospace — 'JetBrains Mono', ui-monospace, monospace",
    textScale: 'Text size',
    radius: 'Roundness',
    preview: 'Live preview',

    // Tooltips — what each control changes, in plain language
    tipPresets:
      'One click sets a whole look — brand colour, font and corner style together. A quick way to feel the range before fine-tuning.',
    tipBrand:
      'The main accent: primary buttons, links, selected items, focus rings and highlights across the whole app.',
    tipSemantic:
      'The colours of feedback — success, warning, danger and info states on tags, badges, alerts and status dots. Neutral follows your text/border colours; primary follows the brand colour.',
    tipTokens:
      'Background layers (page → cards), the shades of body text, and the lines that divide and outline things.',
    tipTypography: 'The interface font and the overall text size, everywhere from menus to chat replies.',
    tipShape: 'How rounded corners are — buttons, inputs, cards, tags and dialogs all follow this.',
    tipPreview:
      'Real components, updating as you tune. Switch between light and dark to check both — each keeps its own colour edits.',
    tipExport:
      'Copies the styles you changed as a block you can drop into your workspace to make them stick. Reset clears the preview.',

    // Export
    exportTitle: 'Save these styles',
    copy: 'Copy styles',
    copied: 'Copied',
    reset: 'Reset',
    noChanges: 'No changes yet — pick a starting style or adjust a colour to build the export.',

    // Preview canvas — section titles
    pvTypography: 'Type',
    pvControls: 'Buttons & inputs',
    pvSemantic: 'Status colours',
    pvChat: 'Chat',

    // Preview canvas — Greenhouse-voiced sample content
    sampleHeading: 'Your workspace, your look',
    sampleBody:
      'Greenhouse gives every team an AI agent that works across chat, the knowledge base and projects. This is how your body copy reads to the people you invite.',
    sampleMeta: 'Updated just now · by the agent',
    samplePrimaryBtn: 'New chat',
    sampleInput: 'Ask the agent anything…',
    sampleToggle: 'Share with team',
    sampleOnline: 'Connected',
    sampleRunning: 'Running',
    sampleFailed: 'Needs attention',
    sampleUserMsg: 'Summarise last quarter and group it by product line.',
    sampleAssistantMsg: 'Revenue grew across the board last quarter. The standouts:',
    sampleBullet1: 'strongest growth, led by enterprise renewals',
    sampleBullet2: 'up sharply on new signups — see the',
    sampleLink: 'usage report',
    sampleModelMeta: 'Answered in 3.8s',

    // Workspace persistence (branding.* workspace settings)
    workspaceTitle: 'Workspace identity',
    tipWorkspace:
      'Saved to the workspace and applied for everyone from the login screen on — tenant name, logo, these styles and the team Sprouty. No code changes, no restart.',
    productName: 'Product name',
    logo: 'Logo',
    logoUpload: 'Upload',
    logoRemove: 'Remove logo',
    logoHint: 'PNG / JPEG / SVG / WebP, up to 256 KB. Shown in the sidebar and on the login screen.',
    logoBadType: 'Unsupported image type',
    logoTooLarge: 'Image exceeds 256 KB',
    teamSprouty: 'Team Sprouty',
    teamSproutyOn: 'Customize',
    teamSproutyOff: 'Use default',
    teamSproutyHint:
      'The default mascot worn by the built-in agents. Customize it to give your workspace its own face.',
    saveWorkspace: 'Save to workspace',
    clearWorkspace: 'Reset workspace branding',
    workspaceSaved: 'Workspace branding saved',
    workspaceCleared: 'Workspace branding reset',
  },

  // Shared Sprouty avatar designer (profile editor + Branding Studio)
  sproutyDesigner: {
    color: 'Color',
    customColor: 'Custom',
    bodyColor: 'Body',
    leafColor: 'Leaf',
    faceStyle: 'Face',
    accessories: 'Accessories',
    slot_hat: 'Hat',
    slot_glasses: 'Glasses',
    slot_held: 'Held',
    none: 'None',
    leafStyle: 'Leaf Style',
  },

  // Runtime Config (admin) — registry-driven workspace settings
  runtimeConfig: {
    intro:
      'Configure the runtime credentials from the admin console — model endpoints, vision, image generation and web search. Values saved here win over environment variables and apply immediately; clearing a field falls back to the environment.',
    group_llm: 'Main LLM',
    group_vision: 'Vision',
    group_image_gen: 'Image generation',
    group_search: 'Web search',
    sourceDb: 'Workspace',
    sourceEnv: 'Environment',
    sourceNone: 'Not set',
    envFallback: 'Env fallback:',
    secretPlaceholder: 'Enter a new value to replace',
    secretSet: 'Configured',
    clear: 'Clear',
    save: 'Save changes',
    saved: 'Runtime config saved',
  },

  // Chat
  chat: {
    newConversation: 'New Conversation',
    history: 'History',
    new: '+ New',
    startConversation: 'Start a conversation',
    defaultDescription: 'Ask a question or search the knowledge base',
    tryAsking: 'Try asking',
    askPlaceholder: 'Ask a question...',
    askPlaceholderFull: 'Ask a question... (@ to mention, / for commands)',
    useSkillsDirective: 'Please use the following skills for this task: {skills}.',
    searchConversations: 'Search conversations...',
    noConversationsFound: 'No conversations found',
    sessionHistory: 'Session history',
    loadingHistory: 'Loading history...',
    sessionMovedToTrash: 'Session moved to trash',
    sessionPermanentlyDeleted: 'Session permanently deleted',
    deleteSessionConfirm: 'Permanently delete this session? This cannot be undone.',
    deleteSessionTitle: 'Permanently delete this session?',
    deleteCannotUndo: 'This cannot be undone.',
    fastThinking: 'Fast — quicker responses, less thorough reasoning',
    deepThinking: 'Deep — slower responses, more thorough reasoning',
    jumpToLatest: 'Jump to latest',
    newContent: 'New content',
    streaming: 'Streaming...',
    howCanIHelp: 'How can I help?',
    giveOverview: 'Give me an overview of what you can help with.',
    hereAreAnswers: 'Here are my answers to your questions:',
    translateToEnglish: 'Translate to English',
    pleaseTranslate: 'Please translate the above content to English...',
    editMessage: 'Edit message',
    regenerate: 'Regenerate',
    regenerateResponse: 'Regenerate response',
    regenerateConfirm: 'Regenerate this response?',
    regenerateWarning: 'The current response will be replaced with a new one.',
    newResponse: 'New response',
    goodResponse: 'Good response',
    badResponse: 'Bad response',
    rateAndComment: 'Rate & Comment',
    yourNote: 'Your note…',
    yourNoteOptional: 'Your note (optional)…',
    writeComment: 'Write a comment...',
    addAnnotation: 'Add annotation',
    addNote: 'Add note',
    editNote: 'Edit note',
    clearAnnotations: 'Clear all annotations',
    quoteAndNote: 'Quote & add note',
    clearRatingFilter: 'Clear rating filter',
    clickToEditName: 'Click to edit name',
    analyzedImage: 'Analyzed image',
    queuing: 'Queuing…',
  },

  // Agent panel
  agent: {
    globalAssistant: 'Global assistant',
    noTasksYet: 'No tasks yet',
  },

  // History
  history: {
    title: 'History',
    searchPlaceholder: 'Search conversations...',
    viewAll: 'View All',
  },

  // Projects
  projects: {
    title: 'Projects',
    tasks: 'Tasks',
    list: 'List',
    board: 'Board',
    gantt: 'Gantt',
    dragToCreate: 'Drag to create a new task',
    deleteTaskConfirm: 'Delete this task?',
    deleteTaskDescription: 'Child tasks will also be deleted. This cannot be undone.',
    taskDeleted: 'Task deleted',
    todo: 'Todo',
    inProgress: 'In Progress',
    inReview: 'In Review',
    onHold: 'On Hold',
    planning: 'Planning',
    cards: 'Cards',
    globalGantt: 'Timeline',
    allProjects: 'All Projects',
    expandAll: 'Expand All',
    collapseAll: 'Collapse All',
    noTasks: 'No tasks',
    projectProgress: 'Progress',
    createProject: 'Create Project',
    editProject: 'Edit Project',
    projectName: 'Project name',
    projectNameRequired: 'Project name is required',
    projectNamePlaceholder: 'Project name *',
    projectDescPlaceholder: 'Project description (optional)',
    projectColor: 'Project color',
    creating: 'Creating...',
    milestone: 'Milestone',
    addTask: 'Add Task',
    changelog: 'Changelog',
    statusUpdated: 'Status updated',
    taskStatusUpdated: 'Status updated',
    movedToProject: 'Moved {count} tasks to "{target}"',
    tasksUpdated: 'Updated {count} tasks',
    taskMovedToProject: 'Task moved to {target}',
    members: 'Members',
    member: 'Member',
    owner: 'Owner',
    selectMember: 'Select user...',
    memberAdded: 'Member added',
    memberRemoved: 'Member removed',
    roleUpdated: 'Role updated',
    removeMember: 'Remove member',
    removeMemberConfirm: 'Remove member?',
    removeMemberConfirmDesc: 'Remove this member from the project:',
    setOwner: 'Set as owner',
    setMember: 'Set as member',
    visibility: 'Visibility',
    visibilityPublic: 'Public',
    visibilityPrivate: 'Private',
    visibilityPublicDesc: 'Visible to all team members',
    visibilityPrivateDesc: 'Only visible to project members',
  },

  // Task management
  task: {
    createTask: 'Create Task',
    createSubtask: 'Create Subtask',
    createMilestone: 'Create Milestone',
    taskTitle: 'Task title',
    taskTitleRequired: 'Please enter a task title',
    milestoneNameRequired: 'Please enter a milestone name',
    titlePlaceholder: 'Task title *',
    milestonePlaceholder: 'Milestone name *',
    descPlaceholder: 'Description (optional)',
    milestoneHint: 'Milestones mark key project nodes, such as release dates or review dates',
    milestoneDate: 'Milestone Date',
    estimatedHours: 'Estimated hours (h)',
    tagsComma: 'Tags (comma-separated)',
    parentTask: 'Parent Task',
    subtasks: 'Subtasks',
    comments: 'Comments',
    dependencies: 'Dependencies',
    editDeps: 'Edit',
    doneDeps: 'Done',
    blockers: 'Blockers (block this task)',
    dependents: 'Dependents (blocked by this task)',
    searchTasksToAdd: 'Search tasks to add blocker...',
    noMatchingTasks: 'No matching tasks',
    noDependencies: 'No dependencies',
    deleteConfirm: 'Delete this task?',
    deleteConfirmDesc: 'Child tasks will also be deleted. This cannot be undone.',
    deleted: 'Task deleted',
    setMilestone: 'Set as Milestone',
    unsetMilestone: 'Unset Milestone',
    addSubtask: 'Add Subtask',
    reparent: 'Make Subtask',
    removeParent: 'Remove Parent',
    moveToProject: 'Move to Project',
    setBlocker: 'Set Blocker',
    deleteTask: 'Delete Task',
    editTask: 'Edit Task',
    taskTitleLabel: 'Task title',
    descriptionMarkdown: 'Description (Markdown)',
  },

  // Settings
  settings: {
    title: 'Settings',
    users: 'Users',
    prompts: 'Prompts',
    agentProfiles: 'Agent Profiles',
    featureRequests: 'Feature Requests',
    llmUsage: 'LLM Usage',
    roles: 'Roles',
    userNotFound: 'User not found',
    noUsersFound: 'No users found',
    noFeatureRequests: 'No feature requests',
    promptDeleted: 'Prompt deleted',
    newPrompt: 'New Prompt',
    editPrompt: 'Edit Prompt',
    egHermesAgent: 'e.g. Hermes Agent',
    egTranslateToEnglish: 'e.g. Translate to English',
    addUser: '+ Add User',
    addNewUser: 'Add new user',
    editUser: 'Edit User',
    resetPassword: 'Reset Password',
    emailPlaceholder: 'Email *',
    passwordPlaceholder: 'Password * (min 8 chars)',
    nicknamePlaceholder: 'Nickname *',
    dailyMsgLimit: 'Daily message limit',
    monthlyTokenLimit: 'Monthly token limit',
    fillRequiredFields: 'Please fill all required fields',
    passwordMinLength: 'Password must be at least 8 characters',
    passwordReset: 'Password reset',
    resetFailed: 'Reset failed',
    enterNewPassword: 'Enter new password (min 8 chars)',
    toolAssignment: 'Tool Assignment',
    toolAssignTitle: 'Tool Assignment — {name}',
    toolAssignHint:
      'Assign available tools to this user. Global tools are always available and do not need assignment.',
    loadToolsFailed: 'Failed to load tool data',
    toolsSaved: 'Tool assignment saved',
    disable: 'Disable',
    enable: 'Enable',
    disableUserConfirm: 'Are you sure you want to {action} user {name}?',
    totalUsers: 'Total {count} users',
    publicGlobal: 'Public (Global)',
    filterByRole: 'All Roles',
    filterByStatus: 'All Status',
    sortByDefault: 'Default',
    sortByTokenUsage: 'Token Usage',
    sortByTotalCalls: 'Total Calls',
    sortByLastLogin: 'Last Login',
    deleteUser: 'Delete',
    deleteUserConfirm: 'Are you sure you want to permanently delete user {name}? This action cannot be undone.',
    userDeleted: 'User deleted',
    deleteFailed: 'Delete failed',
    searchUsers: 'Search name or email...',
  },

  // Knowledge base (docs + nav + sharing + versions)
  knowledge: {
    // Nav panel sections
    internalKb: 'Internal Knowledge Base',
    personalKb: 'Personal Knowledge Base',
    sharedWithMe: 'Shared with me',
    newDoc: 'New Document',
    renameSpace: 'Rename category',
    noDocs: 'No documents yet',
    noPersonalDocs: 'No personal documents yet',
    noSharedDocs: 'No shared documents yet',
    // List / search placeholders
    searchDocs: 'Search documents...',
    searchSharedDocs: 'Search shared documents...',
    searchPersonalDocs: 'Search personal documents...',
    emptyInternalDesc: 'Create your first internal knowledge document.',
    emptyPersonalDesc: 'Create your first personal knowledge document.',
    emptySharedDesc: 'Documents others share with you will appear here.',
    noArchivedDocs: 'No archived documents',
    noArchivedDesc: 'Archived documents will appear here.',
    docsCount: '{count} docs',
    newDocBtn: 'New Doc',
    statusActive: 'Active',
    // Detail / actions
    editable: 'Editable',
    readOnly: 'Read-only',
    history: 'History',
    versionHistory: 'Edit History',
    share: 'Share',
    updatedPrefix: 'Updated',
    onThisPage: 'On this page',
    expandToc: 'Expand outline',
    collapseToc: 'Collapse outline',
    commonQuestions: 'Common Questions',
    relatedTopics: 'Related Topics',
    archiveConfirmTitle: 'Archive document?',
    archiveConfirmTeam: 'Archive "{name}"? It will be hidden from published docs and Agent search.',
    archiveConfirmSimple: 'Archive "{name}"?',
    // Editor
    editDocTitle: 'Edit knowledge doc',
    newDocTitle: 'New knowledge doc',
    fieldTitle: 'Title',
    titlePlaceholder: 'Document title',
    fieldVisibility: 'Visibility',
    visTeam: 'Team (internal)',
    visPrivate: 'Private (personal)',
    fieldStatus: 'Status',
    advancedOptions: 'Advanced (Space, Tags, Summary — auto-filled when left blank)',
    fieldSpace: 'Space',
    fieldTags: 'Tags, comma separated',
    fieldSummary: 'Summary',
    summaryPlaceholder: 'Auto-extracts first 160 chars of body if left blank',
    titleRequired: 'Title is required',
    // Versions dialog
    loadingVersions: 'Loading versions…',
    noVersions: 'No versions yet.',
    currentTag: '(current)',
    updatedReason: 'Updated',
    viewChange: 'View this change',
    restoreToVersion: 'Restore to this version',
    restoredToVersion: 'Restored to v{version}',
    restoreFailed: 'Restore failed',
    initialVersion: 'Initial version',
    fieldContent: 'Content',
    charsWord: 'chars',
    diffInline: 'Inline',
    diffSplit: 'Split',
    beforeLabel: 'Before',
    afterLabel: 'After',
    noContentChange: 'No text change (metadata only)',
    // Share dialog
    shareDocTitle: 'Share Document',
    sharingLabel: 'Sharing:',
    sharedWith: 'Shared with',
    revoke: 'Revoke',
    permission: 'Permission',
    roleReader: 'Read-only (reader)',
    roleEditor: 'Editable (editor)',
    groups: 'Groups',
    noGroupsHint: 'No groups yet — create one to share with a whole team at once.',
    createGroupLink: 'Create a group',
    searchMembers: 'Search members...',
    noMembers: 'No members',
    alreadyShared: 'Shared',
    shareSuccess: 'Shared successfully',
    shareFailed: 'Share failed',
    revokeFailed: 'Revoke failed',
    loadShareFailed: 'Failed to load sharing',
    // Toasts
    loadFailed: 'Failed to load knowledge docs',
    docNotFound: 'Knowledge document not found',
    docCreated: 'Document created',
    docUpdated: 'Document updated',
    docArchived: 'Document archived',
    docRestored: 'Document restored',
    saveFailed: 'Failed to save document',
    archiveFailed: 'Failed to archive document',
    restoreDocFailed: 'Failed to restore document',
    createFailed: 'Failed to create document',
    spaceRenamed: 'Category renamed — {count} docs moved',
    renameSpaceFailed: 'Failed to rename category',
  },

  // Session groups (per-user conversation folders + Pinned)
  sessionGroups: {
    pinned: 'Pinned',
    pin: 'Pin',
    unpin: 'Unpin',
    moveToGroup: 'Move to group',
    removeFromGroup: 'Remove from group',
    ungrouped: 'Ungrouped',
    newGroup: 'New group',
    manageGroups: 'Manage groups',
    groupName: 'Group name',
    noGroups: 'No groups yet. Create your first group below.',
    created: 'Group created',
    updated: 'Group updated',
    deleted: 'Group deleted',
    deleteTitle: 'Delete group?',
    deleteDescription: 'Group "{name}" will be removed. Its sessions are not deleted — they return to the date list.',
    moved: 'Moved',
    reorderFailed: 'Failed to reorder',
    dropHere: 'Drop here',
    showMore: 'Show more',
  },

  // Groups (knowledge-sharing groups)
  groups: {
    intro:
      'Groups are targets for fine-grained knowledge sharing. Share a personal knowledge doc with a group and all its members can access it. Groups you create are managed by you.',
    newGroupName: 'New group name…',
    createGroup: 'Create group',
    noGroups: 'No groups yet.',
    deleteGroup: 'Delete group',
    deleteGroupTitle: 'Delete group?',
    deleteGroupConfirm: 'Delete "{name}"? Access shared with this group will also be revoked.',
    memberOne: 'member',
    memberOther: 'members',
    noMembers: 'No members',
    remove: 'Remove',
    searchToAdd: 'Search members to add…',
    noMatchingMembers: 'No matching members',
    loadFailed: 'Failed to load groups',
    created: 'Group created',
    createFailed: 'Create failed',
    deleted: 'Group deleted',
    deleteFailed: 'Delete failed',
    loadMembersFailed: 'Failed to load members',
    addFailed: 'Add failed',
    removeFailed: 'Remove failed',
  },

  // Skill Center (settings)
  skillCenter: {
    hint: 'The org-wide library of agent skills. Members publish skills from their own AI tools via chat or the Greenhouse MCP (skill_query / skill_mutation); browse, audit and manage them here. Every version is immutable and carries a changelog.',
    empty: 'No skills published yet — ask your agent to publish one via the Greenhouse MCP.',
    searchPlaceholder: 'Search skills…',
    colSkill: 'Skill',
    colDescription: 'Description',
    colTags: 'Tags',
    colVersion: 'Latest',
    colDownloads: 'Downloads',
    colStatus: 'Status',
    colUpdated: 'Updated',
    statusActive: 'Active',
    statusArchived: 'Archived',
    archive: 'Archive',
    unarchive: 'Unarchive',
    archived: 'Skill archived',
    unarchived: 'Skill restored',
    actionFailed: 'Action failed',
    owner: 'Owner',
    createdAt: 'Created',
    versionHistory: 'Version history',
    files: 'files',
    viewFiles: 'View files',
    hideFiles: 'Hide files',
    downloadJson: 'Download JSON',
    by: 'by',
    binaryFile: 'binary, base64',
  },

  // Software update (desktop)
  updates: {
    title: 'Software Update',
    desktopOnly: 'Software update is only available in the desktop app.',
    description:
      'Check for and install new versions from the self-hosted update source. UI hot-updates download silently and apply on restart.',
    checking: 'Checking…',
    checkUpdates: 'Check for Updates',
    appVersion: 'App version',
    uiHotUpdate: 'UI hot-update',
    builtinBaseline: 'Built-in baseline',
    updatedTo: 'UI updated to v{version}, restart to apply',
    upToDate: 'Already up to date',
    checkFailed: 'Update check failed: {error}',
  },

  // Automations (scheduled tasks)
  automations: {
    countLabel: '{count} automations',
    create: 'Create automation',
    createTitle: 'Create Automation',
    editTitle: 'Edit Task',
    noneTitle: 'No automations yet',
    noneDesc: 'Create a scheduled task to have an Agent run automatically at set times.',
    taskName: 'Task Name',
    taskNamePlaceholder: 'Daily ticket analysis',
    profile: 'Profile',
    profileHint: 'Profile determines which tools the Agent can use',
    taskPrompt: 'Task Prompt',
    taskPromptPlaceholder: 'Analyze all of today’s service tickets, focusing on...',
    schedule: 'Schedule',
    advancedSettings: 'Advanced Settings',
    maxSteps: 'Max Steps (1-20)',
    timezone: 'Timezone',
    createAndEnable: 'Create & Enable',
    colTask: 'Task',
    colSchedule: 'Schedule',
    colProfile: 'Profile',
    colStatus: 'Status',
    colRuns: 'Runs',
    colLastRun: 'Last Run',
    colActions: 'Actions',
    runNow: 'Run now',
    viewHistory: 'View history',
    deleteTaskTitle: 'Delete Task',
    deleteTaskConfirm: 'Delete "{name}"? Historical sessions will be preserved.',
    runFailed: 'Run failed',
    saveFailed: 'Save failed',
    // Schedule builder
    freqDaily: 'Daily',
    freqWeekdays: 'Weekdays',
    freqWeekly: 'Weekly',
    freqMonthly: 'Monthly',
    freqCron: 'Cron expression',
    timeLabel: 'Time',
    monthDayLabel: 'Day',
    monthDayOption: 'Day {day}',
    cronHint: 'Standard 5-field cron expression: minute hour day month weekday',
    descDaily: 'Runs every day at {time}',
    descWeekdays: 'Runs on weekdays (Mon–Fri) at {time}',
    descWeekly: 'Runs every {days} at {time}',
    descMonthly: 'Runs on day {day} of each month at {time}',
    weekdaySun: 'Sun',
    weekdayMon: 'Mon',
    weekdayTue: 'Tue',
    weekdayWed: 'Wed',
    weekdayThu: 'Thu',
    weekdayFri: 'Fri',
    weekdaySat: 'Sat',
  },

  // LLM Gateway admin
  llmGateway: {
    title: 'AI Gateway',
    description:
      'Internal users access these models through the server relay — no personal keys needed. Configure upstream providers, publish models, and govern issued keys here.',
    upstreams: 'Upstreams',
    addUpstream: 'Add Upstream',
    editUpstream: 'Edit Upstream',
    deleteUpstreamTitle: 'Delete upstream?',
    deleteUpstreamDesc: 'Removes "{name}" and all models mapped to it.',
    noUpstreams: 'No upstreams configured.',
    addUpstreamFirst: 'Add an upstream first.',
    colName: 'Name',
    colKind: 'Kind',
    colBaseUrl: 'Base URL',
    colKey: 'Key',
    colStatus: 'Status',
    colActions: 'Actions',
    keySet: 'Set',
    keyNone: 'None',
    enabled: 'Enabled',
    off: 'Off',
    fieldName: 'Name',
    providerKind: 'Provider kind',
    baseUrlLabel: 'Base URL (include /v1 if the upstream needs it)',
    apiKeyLabel: 'API key {note}',
    apiKeyKeepNote: '(leave blank to keep)',
    enabledLabel: 'Enabled',
    anthropicNote: 'Anthropic upstreams are stored but not yet served by the gateway (protocol translation pending).',
    models: 'Models',
    addModel: 'Add Model',
    editModel: 'Edit Model',
    deleteModelTitle: 'Delete model?',
    deleteModelDesc: 'Removes "{name}" from the catalog.',
    noModels: 'No models published.',
    colPublicId: 'Public ID',
    colDisplay: 'Display',
    colUpstream: 'Upstream',
    colUpstreamModel: 'Upstream model',
    colFlags: 'Flags',
    flagOn: 'On',
    flagOff: 'Off',
    flagDefault: 'Default',
    flagPublic: 'Public',
    publicIdLabel: 'Public ID (what users select)',
    displayName: 'Display name',
    upstreamLabel: 'Upstream',
    upstreamModelId: 'Upstream model id',
    defaultSeamless: 'Default (seamless)',
    publicSubset: 'Public subset',
    sort: 'Sort',
    gatewayKeys: 'Gateway Keys',
    colUser: 'User',
    colTodayTokens: 'Today tokens',
    colDailyLimit: 'Daily limit',
    noKeys: 'No gateway keys issued yet.',
    revoke: 'Revoke',
    enable: 'Enable',
  },

  // Inbox (shared sessions)
  inbox: {
    title: 'Inbox',
    sharedWithMe: 'Sessions shared with me',
    unreadCount: '{count} unread',
    markAllRead: 'Mark all read',
    noShares: 'No shared sessions',
    noSharesDesc: 'When someone shares a session with you, it will appear here',
    sharedYouSuffix: 'shared with you',
  },

  // User menu / account
  userMenu: {
    usageStats: 'Usage Stats',
    todayMessages: 'Today messages',
    monthTokens: 'This month tokens',
    loadingUsage: 'Loading usage...',
    usageUnavailable: 'Usage unavailable',
    noUsageData: 'No usage data',
    inbox: 'Inbox',
    onlineUsers: 'Online Users',
    onlineUsersTitle: 'Online Users ({count})',
    noOnlineUsers: 'No online users',
    availableProfiles: 'Available Profiles',
  },

  // Media preview dialog
  media: {
    viewAttachment: 'View attachment {current} / {total}',
    prev: 'Previous',
    next: 'Next',
    download: 'Download',
    imageLoadFailed: 'Failed to load image',
    tryDownload: 'Try download',
    videoNotSupported: 'Your browser does not support video playback',
    unknownFile: 'Unknown file',
    downloadFile: 'Download file',
  },

  // MCP selector
  mcp: {
    selectedFailure: 'A selected MCP server is unreachable — open to see details',
    selectServers: 'Select MCP servers to use in this chat',
    injectHint: 'Selected MCP servers are injected into the Agent when you start a new chat.',
    recheck: 'Re-check connectivity',
    loading: 'Loading…',
    connectFailed: 'Connection failed',
    probing: 'Probing…',
    reachable: 'Reachable',
  },

  // Desktop pet status labels
  pet: {
    localRuntimeUnavailable: 'Local runtime unavailable',
    startFailed: 'Failed to start',
    cloudTaskRunning: 'Cloud task running…',
    done: 'Done',
    errored: 'Something went wrong',
  },

  // Local file cards
  localFiles: {
    openFileFailed: 'Failed to open file',
    revealFailed: 'Failed to open file location',
    doubleClickToOpen: 'Double-click to open file',
    opening: 'Opening',
    open: 'Open',
    revealLocation: 'Open file location',
  },

  // Dashboard FAQ categories
  faq: {
    errorCode: 'Error Codes',
    productIssue: 'Product Issues',
    commonQuestion: 'Common Questions',
  },

  toolSpawn: {
    subSession: 'Sub-session',
    running: 'Running…',
    done: 'Done',
    open: 'Open',
  },

  // Generated file download card (export_table, etc.)
  exportFile: {
    download: 'Download',
    expired: 'Expired',
    expiredHint: 'This download link has expired. Ask again to regenerate the file.',
  },

  // Chat message actions
  messageActions: {
    translateToChinese: 'Translate to Chinese',
    zh: 'CN',
  },

  // Health indicator
  health: {
    connected: 'Connected · {model} · {pages} sources',
    unreachable: 'API unreachable',
    checking: 'Checking...',
  },

  // Tool calls
  tools: {
    adminNote: 'Admin note...',
    adminNotesAbout: 'Admin notes about this response...',
  },

  // PDF export
  pdf: {
    exportAsPdf: 'Export as PDF',
  },

  // Slash commands / mentions
  slashCommands: {
    createPromptIn: ' in Prompts',
  },

  // Profile editor
  profileEditor: {
    descriptionPlaceholder: "Brief description of this profile's purpose",
    inheritsModel: 'Inherits model configuration from the base profile',
  },
} as const;
